import React, { useState } from 'react';

import { Box, Button, Stack } from '@mui/material';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';

import { classes } from './styles';
import { theme } from '../../../ThemeContext/ThemeObject';
import CircularProgressWithLabel from '../CustomUIElements/CircularProgressWithLabel';
import UploadSVG from '../Graphics/UploadSVG';

import type { Dispatch, SetStateAction } from 'react';
import type { FileError, FileWithPath } from 'react-dropzone';

const AttachmentSelector = ({
  performUpload,
  uploadUrl,
  onUploadComplete,
  setFileList,
  maxSize,
  maxFiles,
  heightOverride,
  attachment,
  compact,
  maxVideoSize,
  buttonId,
  setIsEdited
}: {
  performUpload: boolean;
  uploadUrl?: string;
  /**
   * Called after each successful upload with the uploaded file's name and the upload
   * endpoint's response body, so callers can act on the record it created.
   */
  onUploadComplete?: (uploaded: { fileName: string; responseData: unknown }) => void;
  setFileList?: Dispatch<SetStateAction<FileWithPath[]>>;
  maxSize: number;
  maxFiles?: number;
  heightOverride?: number | string;
  attachment?: boolean;
  compact?: boolean;
  maxVideoSize?: number;
  buttonId?: string;
  setIsEdited?: Dispatch<SetStateAction<boolean>>;
}) => {
  const getMaxSize = (fileType: string): number => {
    // Audio shares the video limit, matching Asset#validate_file_size.
    if ((fileType.startsWith('video/') || fileType.startsWith('audio/')) && maxVideoSize) {
      return maxVideoSize;
    }
    return maxSize;
  };
  const sizeValidator = (file: File): FileError | null => {
    const maximumSize = getMaxSize(file.type);
    if (file.size > maximumSize) {
      return {
        code: 'file-too-large',
        message: attachment ? 'Files must be under specified size.' : 'Files must be under 50 MB'
      };
    }
    return null;
  };
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [percentCompleted, setPercentCompleted] = useState(0);
  const [error, setError] = useState('');

  const uploadFiles = async (file: FileWithPath) => {
    if (!uploadUrl || !onUploadComplete) {
      return;
    }
    setIsLoading(true);
    const formData = new FormData();
    formData.append('asset', file);
    try {
      const response = await axios.post(uploadUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          setPercentCompleted(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      });
      if (response.status === 201 || response.status === 200) {
        setError('');
        onUploadComplete({ fileName: file.name, responseData: response.data });
      }
    } catch (uploadError) {
      const errors = axios.isAxiosError<{ errors?: string | string[]; message?: string }>(
        uploadError
      )
        ? (uploadError.response?.data?.errors ?? uploadError.response?.data?.message)
        : undefined;
      const message = Array.isArray(errors) ? errors.join(', ') : errors;
      setError(
        message === 'Invalid file.'
          ? 'Unsupported file type'
          : (message ?? 'The file could not be uploaded. Please try again.')
      );
    } finally {
      setIsRejected(false);
      setIsLoading(false);
      setPercentCompleted(0);
    }
  };
  const { getRootProps, getInputProps, open, fileRejections } = useDropzone({
    noClick: true,
    noKeyboard: true,
    validator: sizeValidator,
    maxFiles: maxFiles ?? 0,
    onDragOver: () => {
      setIsDraggedOver(true);
    },
    onDragLeave: () => {
      setIsDraggedOver(false);
    },
    onDropRejected: () => {
      setIsRejected(true);
    },
    onDrop: (acceptedFiles: FileWithPath[]) => {
      setIsEdited?.(true);
      const parsedFiles = acceptedFiles.map(
        (file) => new File([file], file.name.replace(/[()]/g, ''), { type: file.type })
      );
      if (performUpload) {
        parsedFiles.forEach((file) => {
          uploadFiles(file);
        });
      }
      if (setFileList) {
        // append new files to the existing file list
        setFileList((prev) => [...prev, ...parsedFiles]);
      }
      setIsDraggedOver(false);
      setIsRejected(false);
    }
  });

  const rejectionMessage = attachment
    ? 'Files must be under specified size.'
    : 'Files must be under 50 MB';

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        {...getRootProps()}
        sx={{
          ...classes.dragArea,
          ...(isDraggedOver ? classes.dragAreaOver : {}),
          ...(heightOverride ? { height: heightOverride } : {}),
          ...(compact ? { margin: 'unset' } : {})
        }}
      >
        <input {...getInputProps()} />
        {isLoading ? (
          <Stack
            direction={compact ? 'row' : 'column'}
            spacing={2}
            sx={{
              alignItems: 'center',
              scale: compact ? '0.6' : '1.0'
            }}
          >
            <CircularProgressWithLabel value={percentCompleted} />
            <Box sx={{ marginTop: '12px' }}>Uploading...</Box>
          </Stack>
        ) : (
          <Stack
            direction={compact ? 'row' : 'column'}
            spacing={2}
            sx={{
              alignItems: 'center',
              scale: '0.8'
            }}
          >
            <Box sx={{ marginBottom: '12px' }}>
              <UploadSVG />
            </Box>
            {!isDraggedOver ? (
              <>
                <Stack>
                  <Box>{compact ? 'Drag files here or' : 'Drag or choose file to upload'}</Box>
                </Stack>
                {isRejected || error ? (
                  <Box sx={{ color: '#D6827D' }}>{isRejected ? rejectionMessage : error}</Box>
                ) : null}
              </>
            ) : (
              <>
                <Box>
                  {performUpload ? 'Drop files here to upload!' : 'Drop files here to add!'}
                </Box>
                {fileRejections.length > 0 && isDraggedOver ? (
                  <Box sx={{ color: '#D6827D' }}>
                    {fileRejections.map((fileRejection) => (
                      <Box key={fileRejection.file.name}>
                        {fileRejection.errors.map((e) => e.message).join(', ')}
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </>
            )}
            <Button
              className="select-files-button"
              data-testid="select-files-button"
              id={buttonId}
              onClick={open}
              sx={classes.selectFilesButton}
            >
              Choose files
            </Button>
            {attachment ? (
              <Stack
                sx={{
                  fontStyle: 'italic',
                  fontWeight: 200,
                  fontSize: theme.spacing(1.5),
                  textAlign: 'center',
                  paddingTop: theme.spacing(0.5)
                }}
              >
                <Box>Maximum size per file: 50MB</Box>
                {maxVideoSize ? <Box>Maximum size per video or audio file: 500MB</Box> : null}
              </Stack>
            ) : null}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default AttachmentSelector;
